import { Popover, useDisclosure } from '@chakra-ui/react';
import { IUsageTypeResSchema } from '@monetize/types/app';
import { Fragment } from 'react';
import FocusLock from 'react-focus-lock';
import {
  MButton,
  MGrid,
  MGridItem,
  MIDCopyBox,
  MText,
} from '../../../../components/Monetize';
import { FOCUS_WITHOUT_SCROLLING } from '../../../../components/Monetize/Overlays';

interface UsageTypeIDNameProps {
  usageTypes: IUsageTypeResSchema[];
  target?: 'usageTypeId' | 'usageTypeName';
  /**
   * If there is one usage type, props for the id copy box (e.g. show/hide icon, tooltip message, etc.)
   */
  singleIdCopyBoxProps?: Omit<
    React.ComponentProps<typeof MIDCopyBox>,
    'copyValue'
  >;
  /**
   * If there are multiple usage types, props for the id copy boxes (e.g. show/hide icon, tooltip message, etc.)
   */
  multipleIdCopyBoxProps?: Omit<
    React.ComponentProps<typeof MIDCopyBox>,
    'copyValue'
  >;
}

export const UsageTypeIDName = ({
  usageTypes,
  target,
  singleIdCopyBoxProps,
  multipleIdCopyBoxProps,
}: UsageTypeIDNameProps) => {
  const { onOpen, onClose, open: isOpen } = useDisclosure();

  if (!usageTypes || usageTypes.length === 0) {
    return null;
  }

  if (usageTypes.length === 1) {
    if (target === 'usageTypeId') {
      return (
        <MIDCopyBox
          copyValue={usageTypes[0].id}
          tooltipMessage="Copy Usage Type ID to Clipboard"
          displayIcon={false}
          {...singleIdCopyBoxProps}
        />
      );
    } else if (target === 'usageTypeName') {
      return <MText>{usageTypes[0].name}</MText>;
    } else {
      return null;
    }
  }

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(e) => {
        if (e.open) {
          onOpen();
        } else {
          onClose();
        }
      }}
      positioning={{
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MButton
          variant="link"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          fontSize="sm"
        >
          {usageTypes.length > 1 ? 'Multiple' : usageTypes[0].name}
        </MButton>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content
          p={4}
          border="0"
          boxShadow="popover"
          zIndex={10000}
          onClick={(e) => e.stopPropagation()}
        >
          <FocusLock
            focusOptions={FOCUS_WITHOUT_SCROLLING}
            persistentFocus={false}
          >
            <Popover.Arrow mt="1" />
            <MGrid templateColumns={'1fr 1fr'} columnGap={8}>
              <MGridItem py={2}>
                <MText fontWeight="bold">Usage Type ID</MText>
              </MGridItem>
              <MGridItem py={2}>
                <MText fontWeight="bold">Usage Type</MText>
              </MGridItem>
              {usageTypes.map((usageType) => (
                <Fragment key={usageType.id}>
                  <MGridItem py={1}>
                    <MIDCopyBox
                      copyValue={usageType.id}
                      tooltipMessage="Copy Usage Type ID to Clipboard"
                      {...multipleIdCopyBoxProps}
                    />
                  </MGridItem>
                  <MGridItem py={1}>
                    <MText>{usageType.name}</MText>
                  </MGridItem>
                </Fragment>
              ))}
            </MGrid>
          </FocusLock>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
};
